import type {
  AccountsNoTotalCountQuery,
  CreatorStatsQuery,
  DaoCoinStatsQuery,
} from "@/graphql/codegen/graphql";

export const DISCOVER_WIDGET_QUERY_KEY = "discover-widget";
export const DISCOVER_WIDGET_API_LIMIT = 20;
export const DISCOVER_WIDGET_TOP_WALLETS_PRESET = "top-wallets";

export const DISCOVER_WIDGET_CACHE_MAX_AGE_MS = 1000 * 60 * 60 * 24;

export type DiscoverWidgetPreset =
  | "top-coins"
  | "top-earners"
  | "top-joiners"
  | typeof DISCOVER_WIDGET_TOP_WALLETS_PRESET;

export type DiscoverWidgetTimePeriod =
  | "AllTime"
  | "Past1H"
  | "Past24H"
  | "Past7D"
  | "Past30D";

export type DiscoverWidgetCoinItem = NonNullable<
  NonNullable<DaoCoinStatsQuery["daoCoinStats"]>["nodes"][number]
>;

export type DiscoverWidgetJoinerItem = NonNullable<
  NonNullable<AccountsNoTotalCountQuery["accounts"]>["nodes"][number]
>;

export type DiscoverWidgetEarnerItem = NonNullable<
  NonNullable<CreatorStatsQuery["creatorStats"]>["nodes"][number]
>;

export type DiscoverWidgetItem =
  | DiscoverWidgetCoinItem
  | DiscoverWidgetEarnerItem
  | DiscoverWidgetJoinerItem;

export type DiscoverWidgetApiResponse = {
  fetchedAt: number;
  items: DiscoverWidgetItem[];
  limit: number;
  nextPageParam: string | null;
  preset: DiscoverWidgetPreset;
  timePeriod: DiscoverWidgetTimePeriod;
};

export const getDiscoverWidgetCacheMaxAgeMs = (
  timePeriod: DiscoverWidgetTimePeriod,
) => {
  switch (timePeriod) {
    case "Past1H":
      return 1000 * 60 * 5;
    case "Past24H":
      return 1000 * 60 * 30;
    case "Past7D":
      return 1000 * 60 * 60 * 6;
    case "AllTime":
    case "Past30D":
      return DISCOVER_WIDGET_CACHE_MAX_AGE_MS;
  }
};

export const getDiscoverWidgetQueryKey = ({
  preset,
  timePeriod,
}: {
  preset: DiscoverWidgetPreset;
  timePeriod: DiscoverWidgetTimePeriod;
}) => [DISCOVER_WIDGET_QUERY_KEY, preset, timePeriod] as const;

export const getDiscoverWidgetCategoryCacheTag = ({
  preset,
  timePeriod,
}: {
  preset: DiscoverWidgetPreset;
  timePeriod: DiscoverWidgetTimePeriod;
}) => `${DISCOVER_WIDGET_QUERY_KEY}:${preset}:${timePeriod}`;

export const getDiscoverWidgetApiUrl = ({
  pageParam,
  preset,
  refresh = false,
  timePeriod,
}: {
  pageParam?: string | null;
  preset: DiscoverWidgetPreset;
  refresh?: boolean;
  timePeriod: DiscoverWidgetTimePeriod;
}) => {
  const params = new URLSearchParams({
    preset,
    timePeriod,
  });

  if (pageParam) {
    params.set("pageParam", pageParam);
  }

  if (refresh) {
    params.set("refresh", "1");
  }

  return `/client-api/discover-widget?${params.toString()}`;
};
